/**
 * The studio's icon set: 16px, one stroke weight, `currentColor`.
 *
 * Paths rather than a font or a package, so the rail tints them and a screen
 * can drop one into a button without a build step learning about it. Every
 * icon here is used somewhere; add one when a control needs it and not before.
 */
export type IconName =
  | 'home'
  | 'factory'
  | 'work'
  | 'inbox'
  | 'play'
  | 'flow'
  | 'agent'
  | 'link'
  | 'chart'
  | 'sliders'
  | 'search'
  | 'plus'
  | 'sun'
  | 'moon'
  | 'check'
  | 'arrow'
  | 'stop'
  | 'external'
  | 'chevron'
  | 'book'
  | 'sparkle'
  | 'layers'
  | 'menu';

const PATHS: Record<IconName, string> = {
  home: 'M2.6 7.4 8 2.8l5.4 4.6V13a.8.8 0 0 1-.8.8H3.4a.8.8 0 0 1-.8-.8V7.4ZM6.4 13.8V9.4h3.2v4.4',
  factory: 'M2.4 13.6h11.2M3.2 13.6V7.2l3.4 2.2V7.2l3.4 2.2V4.2h3.2v9.4',
  work: 'M2.6 3.4h3.2v9.2H2.6V3.4ZM6.4 3.4h3.2v6.2H6.4V3.4ZM10.2 3.4h3.2v3.6h-3.2V3.4Z',
  inbox: 'M2.4 9.4h3l1 1.8h3.2l1-1.8h3M2.4 9.4 4.2 3.4h7.6l1.8 6v3.2a1 1 0 0 1-1 1H3.4a1 1 0 0 1-1-1V9.4Z',
  play: 'M8 14.4A6.4 6.4 0 1 0 8 1.6a6.4 6.4 0 0 0 0 12.8ZM6.6 5.6l4 2.4-4 2.4V5.6Z',
  flow: 'M8 1.9v3.4M8 10.7v3.4M4.2 8h7.6M8 5.3a1.35 1.35 0 1 0 0 2.7 1.35 1.35 0 0 0 0-2.7ZM3 6.65a1.35 1.35 0 1 0 0 2.7 1.35 1.35 0 0 0 0-2.7ZM13 6.65a1.35 1.35 0 1 0 0 2.7 1.35 1.35 0 0 0 0-2.7Z',
  agent: 'M8 2.6a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4ZM3.6 9.4a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4ZM12.4 9.4a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4ZM8 6v2.2M8 8.2 4.6 9.9M8 8.2l3.4 1.7',
  link: 'M6.6 9.4a2.6 2.6 0 0 0 3.9.3l2-2a2.6 2.6 0 0 0-3.7-3.7l-1.1 1.1M9.4 6.6a2.6 2.6 0 0 0-3.9-.3l-2 2a2.6 2.6 0 0 0 3.7 3.7l1.1-1.1',
  chart: 'M2.4 13.2h11.2M4.2 11.4V7.2M7.4 11.4V4.2M10.6 11.4V8.8M13 11.4V5.6',
  sliders: 'M3 4.6h10M3 8h10M3 11.4h10M6.2 4.6a1.2 1.2 0 1 0 2.4 0 1.2 1.2 0 0 0-2.4 0ZM9.4 11.4a1.2 1.2 0 1 0 2.4 0 1.2 1.2 0 0 0-2.4 0Z',
  search: 'M7 12A5 5 0 1 0 7 2a5 5 0 0 0 0 10ZM10.6 10.6 14 14',
  plus: 'M8 3v10M3 8h10',
  sun: 'M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM8 1.6v1.6M8 12.8v1.6M1.6 8h1.6M12.8 8h1.6M3.5 3.5l1.1 1.1M11.4 11.4l1.1 1.1M3.5 12.5l1.1-1.1M11.4 4.6l1.1-1.1',
  moon: 'M13.4 9.6A5.6 5.6 0 0 1 6.4 2.6a5.6 5.6 0 1 0 7 7Z',
  check: 'M3.2 8.4 6.4 11.6 12.8 4.8',
  arrow: 'M3 8h10M9 4l4 4-4 4',
  stop: 'M8 14.4A6.4 6.4 0 1 0 8 1.6a6.4 6.4 0 0 0 0 12.8ZM6 6h4v4H6V6Z',
  external: 'M6.4 3.4H3.4a1 1 0 0 0-1 1v8.2a1 1 0 0 0 1 1h8.2a1 1 0 0 0 1-1V9.6M9.4 2.4h4.2v4.2M13.6 2.4 7.6 8.4',
  chevron: 'M6 3.6 10.4 8 6 12.4',
  book: 'M3 3.2h4.2c.9 0 1.6.7 1.6 1.6v8c0-.7-.6-1.3-1.4-1.3H3V3.2ZM13 3.2H8.8c-.9 0-1.6.7-1.6 1.6v8c0-.7.6-1.3 1.4-1.3H13V3.2Z',
  sparkle: 'M8 2v3M8 11v3M2.5 8h3M10.5 8h3M4.4 4.4l2 2M9.6 9.6l2 2M11.6 4.4l-2 2M6.4 9.6l-2 2',
  layers: 'M8 1.6 14.4 5 8 8.4 1.6 5 8 1.6ZM2.4 8 8 11 13.6 8M2.4 11 8 14 13.6 11',
  menu: 'M2.6 4.4h10.8M2.6 8h10.8M2.6 11.6h10.8',
};

export function Icon({ name, className = 'icon' }: { name: IconName; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d={PATHS[name]}
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
